import Airtable from 'airtable';
import { OPPORTUNITIES_TABLE } from './opportunityAuditShared.js';
import { isOpportunityTableUrl, queueOpportunityAudit } from './opportunityAuditClient.js';

let instrumented = false;

function unwrapCallback(args) {
  if (typeof args[args.length - 1] === 'function') {
    return { args: args.slice(0, -1), callback: args[args.length - 1] };
  }
  return { args, callback: null };
}

function asRecordArray(result) {
  if (!result) return [];
  return Array.isArray(result) ? result : [result];
}

function parseUpdateTargets(args) {
  const first = args[0];
  if (Array.isArray(first)) {
    return first.map((item) => ({
      id: item.id,
      fields: item.fields || {},
    }));
  }
  if (typeof first === 'string') {
    return [{ id: first, fields: args[1] || {} }];
  }
  return [];
}

function wrapTable(table) {
  if (table.__opportunityAuditWrapped) return table;

  const original = {
    update: table.update.bind(table),
    create: table.create.bind(table),
    destroy: table.destroy.bind(table),
    replace: table.replace ? table.replace.bind(table) : null,
    find: table.find.bind(table),
  };

  const loadBefore = async (id) => {
    try {
      const record = await original.find(id);
      return record?.fields || {};
    } catch {
      return {};
    }
  };

  table.update = function opportunityAuditUpdate(...rawArgs) {
    const { args, callback } = unwrapCallback(rawArgs);
    const work = (async () => {
      const targets = parseUpdateTargets(args);
      const befores = {};
      await Promise.all(
        targets.map(async (target) => {
          if (target.id) befores[target.id] = await loadBefore(target.id);
        })
      );
      const result = await original.update(...args);
      asRecordArray(result).forEach((record) => {
        queueOpportunityAudit({
          action: 'update',
          recordId: record.id,
          before: befores[record.id],
          after: record.fields,
        });
      });
      return result;
    })();
    if (callback) {
      work.then((result) => callback(null, result), callback);
      return undefined;
    }
    return work;
  };

  table.create = function opportunityAuditCreate(...rawArgs) {
    const { args, callback } = unwrapCallback(rawArgs);
    const work = (async () => {
      const result = await original.create(...args);
      asRecordArray(result).forEach((record) => {
        queueOpportunityAudit({
          action: 'create',
          recordId: record.id,
          before: {},
          after: record.fields,
        });
      });
      return result;
    })();
    if (callback) {
      work.then((result) => callback(null, result), callback);
      return undefined;
    }
    return work;
  };

  table.destroy = function opportunityAuditDestroy(...rawArgs) {
    const { args, callback } = unwrapCallback(rawArgs);
    const ids = Array.isArray(args[0]) ? args[0] : [args[0]];
    const work = (async () => {
      const befores = {};
      await Promise.all(
        ids.filter(Boolean).map(async (id) => {
          befores[id] = await loadBefore(id);
        })
      );
      const result = await original.destroy(...args);
      asRecordArray(result).forEach((record) => {
        queueOpportunityAudit({
          action: 'delete',
          recordId: record.id || record,
          before: befores[record.id] || befores[record],
          after: {},
        });
      });
      return result;
    })();
    if (callback) {
      work.then((result) => callback(null, result), callback);
      return undefined;
    }
    return work;
  };

  if (original.replace) {
    table.replace = function opportunityAuditReplace(...rawArgs) {
      const { args, callback } = unwrapCallback(rawArgs);
      const work = (async () => {
        const targets = parseUpdateTargets(args);
        const befores = {};
        await Promise.all(
          targets.map(async (target) => {
            if (target.id) befores[target.id] = await loadBefore(target.id);
          })
        );
        const result = await original.replace(...args);
        asRecordArray(result).forEach((record) => {
          queueOpportunityAudit({
            action: 'update',
            recordId: record.id,
            before: befores[record.id],
            after: record.fields,
          });
        });
        return result;
      })();
      if (callback) {
        work.then((result) => callback(null, result), callback);
        return undefined;
      }
      return work;
    };
  }

  table.__opportunityAuditWrapped = true;
  return table;
}

function instrumentAirtableClient() {
  if (Airtable.prototype.__opportunityAuditPatched) return;
  const originalBase = Airtable.prototype.base;
  Airtable.prototype.base = function opportunityAuditBase(...baseArgs) {
    const rawBase = originalBase.apply(this, baseArgs);
    return new Proxy(rawBase, {
      apply(target, thisArg, tableArgs) {
        const table = target.apply(thisArg, tableArgs);
        if (tableArgs[0] === OPPORTUNITIES_TABLE) return wrapTable(table);
        return table;
      },
    });
  };
  Airtable.prototype.__opportunityAuditPatched = true;
}

function recordIdFromUrl(url) {
  const match = String(url || '').match(/\/(rec[a-zA-Z0-9]{14})(?:\?|$)/);
  return match ? match[1] : null;
}

function parseWriteBody(init = {}) {
  if (!init.body) return {};
  try {
    return typeof init.body === 'string' ? JSON.parse(init.body) : {};
  } catch {
    return {};
  }
}

function instrumentWindowFetch() {
  if (typeof window === 'undefined' || window.__opportunityAuditFetch) return;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    const method = String(init.method || input?.method || 'GET').toUpperCase();
    const skip = init.headers?.['X-Upgraded-Audit-Skip'] || init.headers?.['x-upgraded-audit-skip'];
    const isWrite = ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method);
    if (!isWrite || skip || !isOpportunityTableUrl(url)) {
      return nativeFetch(input, init);
    }

    const body = parseWriteBody(init);
    const recordId = recordIdFromUrl(url) || body?.records?.[0]?.id || null;
    let before = {};
    if (recordId && method !== 'POST') {
      try {
        const existing = await nativeFetch(url.split('?')[0], {
          headers: {
            ...(init.headers || {}),
            'X-Upgraded-Audit-Skip': '1',
          },
        });
        if (existing.ok) {
          const payload = await existing.json();
          before = payload.fields || payload.records?.[0]?.fields || {};
        }
      } catch {
        before = {};
      }
    }

    const response = await nativeFetch(input, init);
    try {
      const clone = response.clone();
      const payload = await clone.json();
      const records = payload.records || (payload.id ? [payload] : []);
      const action = method === 'DELETE' ? 'delete' : method === 'POST' ? 'create' : 'update';
      records.forEach((record) => {
        queueOpportunityAudit({
          action,
          recordId: record.id || recordId,
          before,
          after: record.fields || {},
        });
      });
    } catch {
      // Keep the original response even if audit parsing fails.
    }
    return response;
  };
  window.__opportunityAuditFetch = true;
}

export function instrumentOpportunityWrites() {
  if (instrumented || typeof window === 'undefined') return;
  instrumented = true;
  instrumentAirtableClient();
  instrumentWindowFetch();
}

instrumentOpportunityWrites();
